import { OrgWidgetData, OrgWidgetEntity, OrgWidgetType, OrgWidgetViewer } from '../models/org-widget.models';

/**
 * Who sees what on the organisation dashboard (ISS-022): staff see only what is theirs, org admins
 * see everything. Everything here is PURE and never mutates its input: the widget data is built from
 * NgRx-store objects, which are frozen.
 *
 * A missing viewer means "unscoped": other consumers of the widgets keep seeing everything.
 */

type Rec = Record<string, unknown>;

// ---------------------------------------------------------------------------------------------
// Who is an org admin, and who is "my team"
// ---------------------------------------------------------------------------------------------

/**
 * Org admin = one of the org's owners, OR holds the org-admin permission (the codebase has no
 * dedicated one; `organization/edit-organization-board` is what the dashboard already used), OR is
 * a developer.
 */
export function isOrgAdminViewer(args: {
    uid: string;
    owners: string[] | null | undefined;
    hasAdminPermission: boolean;
    isDeveloper: boolean;
}): boolean {
    return !!args.isDeveloper || !!args.hasAdminPermission || (!!args.uid && (args.owners || []).includes(args.uid));
}

function str(v: unknown): string {
    return typeof v === 'string' ? v : '';
}

function strings(v: unknown): string[] {
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x) : [];
}

function isLive(hat: Rec | undefined | null): hat is Rec {
    return !!hat && hat['isSoftDeleted'] !== true;
}

/** Part keys a hat sits on (`positionArray`, falling back to the legacy `positions[].partKey`). */
function hatPartKeys(hat: Rec): string[] {
    const keys = strings(hat['positionArray']);
    if (keys.length) return keys;
    const legacy = hat['positions'];
    return Array.isArray(legacy) ? legacy.map((p) => str((p as Rec)?.['partKey'])).filter(Boolean) : [];
}

/** Hat entities the person wears (live hats whose `assignedToArray` has them). */
function hatsOf(uid: string, hats: OrgWidgetEntity[]): OrgWidgetEntity[] {
    return (hats || []).filter((h) => isLive(h.data) && strings(h.data['assignedToArray']).includes(uid));
}

/**
 * The viewer plus everyone holding a post BELOW a post the viewer holds.
 *
 * A post is a leaf, so "below" a post means the people in the structure it heads: a viewer who
 * holds a head or deputy post (`headerRole`) leads the post's parent container and all it contains;
 * one who holds a container directly leads that container. A plain post (no header role) has nobody
 * below it. Linked posts own no people and are ignored.
 */
export function computeTeamUids(uid: string, parts: Rec[], hats: { key?: string; data: Rec }[]): string[] {
    const team = new Set<string>([uid]);
    const byKey = new Map<string, Rec>();
    for (const p of parts || []) {
        const k = str(p?.['partKey']);
        if (k) byKey.set(k, p);
    }

    const roots = new Set<string>();
    for (const hat of hats || []) {
        if (!isLive(hat.data) || !strings(hat.data['assignedToArray']).includes(uid)) continue;
        for (const partKey of hatPartKeys(hat.data)) {
            const part = byKey.get(partKey);
            if (!part || str(part['linkedPartKey'])) continue;
            if (str(part['partType']) === 'post') {
                const role = str(part['headerRole']);
                const parent = str(part['parentPartKey']);
                if ((role === 'head' || role === 'deputy') && parent) roots.add(parent);
            } else {
                roots.add(partKey);
            }
        }
    }
    if (roots.size === 0) return [uid];

    const isBelow = (partKey: string, rootKey: string): boolean => {
        const seen = new Set<string>();
        let cur: string | undefined = partKey;
        while (cur && !seen.has(cur)) {
            if (cur === rootKey) return true;
            seen.add(cur);
            cur = str(byKey.get(cur)?.['parentPartKey']) || undefined;
        }
        return false;
    };

    const scopeKeys = new Set<string>();
    for (const k of byKey.keys()) {
        for (const root of roots) {
            if (isBelow(k, root)) {
                scopeKeys.add(k);
                break;
            }
        }
    }

    for (const hat of hats || []) {
        if (!isLive(hat.data)) continue;
        if (!hatPartKeys(hat.data).some((k) => scopeKeys.has(k))) continue;
        strings(hat.data['assignedToArray']).forEach((u) => team.add(u));
    }
    return Array.from(team);
}

// ---------------------------------------------------------------------------------------------
// Widget visibility
// ---------------------------------------------------------------------------------------------

/** Widget types that only org admins may see (for now: Security Coverage). */
const ADMIN_ONLY_WIDGETS: ReadonlySet<OrgWidgetType> = new Set<OrgWidgetType>(['security-coverage']);

export function canSeeWidget(type: OrgWidgetType, viewer: OrgWidgetViewer | undefined): boolean {
    if (!viewer || viewer.isOrgAdmin) return true;
    return !ADMIN_ONLY_WIDGETS.has(type);
}

export function visibleWidgetTypes(types: OrgWidgetType[], viewer: OrgWidgetViewer | undefined): OrgWidgetType[] {
    return types.filter((t) => canSeeWidget(t, viewer));
}

// ---------------------------------------------------------------------------------------------
// Policies
// ---------------------------------------------------------------------------------------------

const POLICY_STATUSES = new Set([
    'draft',
    'pending_approval',
    'approved',
    'scheduled',
    'published',
    'expired',
    'cancelled',
    'revoked',
    'archived',
    'deleted',
]);

/** Same derivation the Policy Status widget uses for its groups. */
function policyStatus(d: Rec): string {
    const status = str(d['status']).toLowerCase();
    if (status && POLICY_STATUSES.has(status)) return status;
    if (d['isPublished']) return 'published';
    if (d['isArchived']) return 'archived';
    return 'draft';
}

function isPublishedPolicy(e: OrgWidgetEntity): boolean {
    return !!e.data && policyStatus(e.data) === 'published';
}

/** Does this (published) policy apply to the person? Org-wide, or resolved/selected to them. */
function policyAppliesTo(d: Rec, uid: string): boolean {
    const targets = d['targets'] as Rec | undefined;
    if (!targets || targets['mode'] === 'all_organization') return true;
    if (strings(targets['affectedUserUids']).includes(uid)) return true;
    const direct = targets['directUsers'];
    return Array.isArray(direct) && direct.some((u) => str((u as Rec)?.['uid']) === uid);
}

// ---------------------------------------------------------------------------------------------
// Per-widget scoping. Each returns the SAME object for an org admin / no viewer.
// ---------------------------------------------------------------------------------------------

function isUnscoped(data: OrgWidgetData): boolean {
    return !data.viewer || data.viewer.isOrgAdmin;
}

function sgKeysOf(hat: Rec): string[] {
    const raw = hat['securityGroupsArray'];
    if (!Array.isArray(raw)) return [];
    return raw.map((e) => (typeof e === 'string' ? e : str((e as Rec)?.['key']))).filter((k): k is string => !!k);
}

/** Organization Overview: staff see only their own details at the top. */
export function scopeOverviewData(data: OrgWidgetData): OrgWidgetData {
    if (isUnscoped(data)) return data;
    const uid = data.viewer!.uid;

    const myHats = hatsOf(uid, data.hats);
    const myPartKeys = new Set(myHats.flatMap((h) => hatPartKeys(h.data)));
    const mySgKeys = new Set(myHats.flatMap((h) => sgKeysOf(h.data)));
    const me = (data.users || []).filter((u) => u.key === uid);
    const myFeatureKeys = new Set<string>([
        ...me.flatMap((u) => strings(u.data?.['features'])),
        ...me.flatMap((u) => Object.keys((u.data?.['featureDetails'] as Rec) || {})),
    ]);

    return {
        ...data,
        users: me,
        hats: myHats,
        positions: (data.positions || []).filter((p) => myPartKeys.has(str(p['partKey']))),
        securityGroups: (data.securityGroups || []).filter((s) => mySgKeys.has(s.key)),
        policies: (data.policies || []).filter((p) => isPublishedPolicy(p) && policyAppliesTo(p.data, uid)),
        features: (data.features || []).filter((f) => myFeatureKeys.has(f.key)),
    };
}

/** Policy Status: staff see only published policies. */
export function scopePolicyStatusData(data: OrgWidgetData): OrgWidgetData {
    if (isUnscoped(data)) return data;
    return { ...data, policies: (data.policies || []).filter(isPublishedPolicy) };
}

/** Policy Acknowledgment: staff see only the policies THEY must acknowledge, with their own progress. */
export function scopeAcknowledgmentData(data: OrgWidgetData): OrgWidgetData {
    if (isUnscoped(data)) return data;
    const uid = data.viewer!.uid;

    const myAcks = (data.policyAcknowledgments || []).filter((a) => str(a.data?.['uid']) === uid);
    const policies = (data.policies || [])
        .filter(
            (p) => isPublishedPolicy(p) && p.data['requiresAcknowledgment'] === true && policyAppliesTo(p.data, uid)
        )
        .map((p) => {
            const done = myAcks.some((a) => str(a.data['policyKey']) === p.key && a.data['isComplete'] === true);
            return {
                key: p.key,
                data: {
                    ...p.data,
                    // The org-wide counters become this person's own: one required, done or not.
                    acknowledgmentStats: { totalRequired: 1, totalAcknowledged: done ? 1 : 0 },
                },
            };
        });

    return {
        ...data,
        policies,
        users: (data.users || []).filter((u) => u.key === uid),
        policyAcknowledgments: myAcks,
    };
}

/** Feature & Permission Map: staff see only the features and permissions they hold. */
export function scopeFeatureMapData(data: OrgWidgetData): OrgWidgetData {
    if (isUnscoped(data)) return data;
    const me = (data.users || []).find((u) => u.key === data.viewer!.uid);
    const details = ((me?.data?.['featureDetails'] as Record<string, Rec>) || {}) as Record<string, Rec>;
    const mine = new Set<string>([...strings(me?.data?.['features']), ...Object.keys(details)]);

    const features = (data.features || [])
        .filter((f) => mine.has(f.key))
        .map((f) => {
            const granted = strings(details[f.key]?.['permissions']);
            if (!granted.length) return f;
            const all = strings(f.data['permissions']);
            return { key: f.key, data: { ...f.data, permissions: all.filter((p) => granted.includes(p)) } };
        });
    return { ...data, features };
}

// ---------------------------------------------------------------------------------------------
// Recent Activity
// ---------------------------------------------------------------------------------------------

export type ActivityEntityKind = 'user' | 'hat' | 'securityGroup' | 'policy' | 'feature';

/**
 * Staff see items about themselves and the people under them: a member in the team, a hat worn by
 * the team, or anything the team changed (`lastChangedBy`, else the older `modifiedBy`/`createdBy`).
 */
export function activityVisibleToViewer(
    kind: ActivityEntityKind,
    key: string,
    data: Rec,
    viewer: OrgWidgetViewer | undefined
): boolean {
    if (!viewer || viewer.isOrgAdmin) return true;
    const team = new Set(viewer.teamUids);

    if (kind === 'user') return team.has(key);
    if (kind === 'hat' && strings(data?.['assignedToArray']).some((u) => team.has(u))) return true;

    const by = (data?.['lastChangedBy'] as Rec | undefined) || undefined;
    const changer = str(by?.['uid']) || str(data?.['modifiedBy']) || str(data?.['createdBy']);
    return !!changer && team.has(changer);
}
